import React, { useMemo, type PropsWithChildren, useEffect } from 'react';
import { TopNav4 } from '@jira/nav4__top-nav/src/ui/index.tsx';
import { getTokenValue } from '@atlaskit/tokens';
import AsyncCustomThemeAdminFlag from '@atlassian/jira-custom-theme-admin-flag/src/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	MobileWebExperimentExposure,
	isMobileAndInMvpOrExistingUsersExperiment,
	MobileWebExperimentViewPanelNewTenantsExposure,
	MobileWebExperimentViewPanelExistingUserExposure,
	isMobileAndInViewPanelIterationExperiment,
} from '@atlassian/jira-mobile-web/src/index.tsx';
import { EditionAwareness } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness/src/ui/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { Nav4RolloutAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-analytics';
import Nav4AppSwitcher from '@atlassian/jira-navigation-apps-topnav4-app-switcher/src/ui/index.tsx';
import {
	CustomThemeDataProvider,
	useSetCustomThemeData,
} from '@atlassian/jira-navigation-apps-common/src/controllers/custom-theme-context/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { CustomThemeAnalytics } from '@atlassian/jira-navigation-apps-common/src/controllers/custom-theme-analytics/index.tsx';
import { useTheme } from '../controllers/theme/main.tsx';
import { ConversationAssistant } from './conversation-assistant/index.tsx';
import { UniversalCreateOrDefaultButton } from './create/UniversalCreateOrDefaultButton.tsx';
import { Create } from './create/main.tsx';
import { Help } from './help/main.tsx';
import messages from './messages.tsx';
import { Notifications } from './notifications/main.tsx';
import { ProductHome } from './product-home/main.tsx';
import { NavigationRouterListener } from './router-listener/index.tsx';
import { Search } from './search/index.tsx';
import { Settings } from './settings/index.tsx';
import { Nav4NotificationsData } from './notifications/Nav4NotificationsData.tsx';

export function AtlassianNavigation(): React.JSX.Element {
	return (
		<>
			<CustomThemeContextWrapper>
				<Nav4TopNav />
			</CustomThemeContextWrapper>
			{/* AsyncNav4RolloutAnalytics fire analytics for both Nav3 and Nav4 */}
			<Nav4RolloutAnalytics />
			{fg('jira_mobile_web_mvp_2_feature_gate') && <MobileWebExperimentExposure />}
			{fg('jira_mobile_web_mvp_view_panel_kill_switch') && (
				<MobileWebExperimentViewPanelNewTenantsExposure />
			)}
			{fg('jira_mobile_web_mvp_view_panel_kill_switch') && (
				<MobileWebExperimentViewPanelExistingUserExposure />
			)}
		</>
	);
}

function Nav4TopNav() {
	const theme = useTheme();
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const isMobileExperience = isMobileAndInMvpOrExistingUsersExperiment();

	// See: https://hello.jira.atlassian.cloud/browse/NAVX-1993
	const setCustomThemeData = useSetCustomThemeData();
	if (fg('jfp-magma-fix-custom-theme-data-hydration-error')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			setCustomThemeData({
				hasCustomBGColor: !!theme?.mode.navigation.backgroundColor,
				hasCustomHighlightColor: getHasCustomHighlightColor(theme),
				cloudId,
			});
		}, [cloudId, setCustomThemeData, theme]);
	} else {
		setCustomThemeData({
			hasCustomBGColor: !!theme?.mode.navigation.backgroundColor,
			hasCustomHighlightColor: getHasCustomHighlightColor(theme),
			cloudId,
		});
	}

	const nav4NavigationProps = useMemo(
		() =>
			isMobileExperience
				? {
						hideSideNavToggleButton: true,
						renderSearch: isMobileAndInViewPanelIterationExperiment() ? Search : undefined,
						renderNotifications: isMobileAndInViewPanelIterationExperiment()
							? Notifications
							: undefined,
					}
				: {
						renderAppSwitcher: Nav4AppSwitcher,
						renderSearch: Search,
						renderEditionAwareness: EditionAwareness,
						renderHelp: Help,
						renderNotifications: Notifications,
						renderConversationAssistant: ConversationAssistant,
						renderSettings: Settings,
					},
		[isMobileExperience],
	);

	return (
		<>
			<TopNav4
				renderProductHome={ProductHome}
				renderCreate={
					fg('navx_onboarding_universal_create_in_jira') ? UniversalCreateOrDefaultButton : Create
				}
				renderNav4Notifications={Nav4NotificationsData}
				{...nav4NavigationProps}
				theme={theme}
				label={formatMessage(messages.topNav)}
			/>
			<NavigationRouterListener />
			{isSiteAdmin && <AsyncCustomThemeAdminFlag />}
		</>
	);
}

function CustomThemeContextWrapper({ children }: PropsWithChildren) {
	return (
		<CustomThemeDataProvider>
			<CustomThemeAnalytics>{children}</CustomThemeAnalytics>
		</CustomThemeDataProvider>
	);
}

function getHasCustomHighlightColor(theme: ReturnType<typeof useTheme>) {
	if (!theme) return false;

	// Default create button background color in Nav4 is #0052CC
	// #0052CC is a legacy brand blue. In the non-legacy tokens, the general replacement is:
	//   • Token name: color.link
	//   • CSS variable: var(--ds-link)
	//   • Current hex in atlassian-light: #0C66E4
	// If the create button background color is different from this value, we consider it as a custom highlight color
	const tokenHighlightColor = getTokenValue('color.link');

	return (
		theme.mode.create.default.backgroundColor !== tokenHighlightColor &&
		// Failsafe check in case the token value is not what we expect
		theme.mode.create.default.backgroundColor !== '#0052CC'
	);
}
